import React, { useEffect, useRef, useState } from "react";
import { Box, Checkbox, CircularProgress, Grid2, useTheme } from "@mui/material";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { Employee } from "../../../../../models/Employees/Employee";
import { TeamWithPermissions } from "../../../../../models/Employees/TeamWithPermissions";
import { PermissionProject } from "../../../../../models/Employees/PermissionProject";

type CopyTeam = {
  id: number;
  name: string;
  is_manager?: boolean;
  permissions: { role_id: number; role: string }[];
};

type CopyEmployeePermissionsProps = {
  permissionProject: PermissionProject;
  resetKey?: number;
  disabled?: boolean;
  teamsWithPermissions: TeamWithPermissions[];
  onEnsureTeamPermissions?: (teamIds: number[]) => Promise<void>;
  onSearchEmployees: (search: string) => Promise<FilterValue[]>;
  onLoadEmployee: (employeeId: number) => Promise<Employee>;
  onCopyTeamsChange: (change: {
    addTeams: { id: number; name: string; is_manager?: boolean }[];
    addPermissions: { role_id: number; role: string }[];
    removeTeamIds: number[];
    removeRoleIds: number[];
  }) => void;
};

const CopyEmployeePermissions: React.FC<CopyEmployeePermissionsProps> = ({
  permissionProject,
  resetKey = 0,
  disabled = false,
  teamsWithPermissions,
  onEnsureTeamPermissions,
  onSearchEmployees,
  onLoadEmployee,
  onCopyTeamsChange,
}) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 400);
  const [employeeOptions, setEmployeeOptions] = useState<FilterValue[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<FilterValue[]>([]);
  const [source, setSource] = useState<Employee | null>(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [copyError, setCopyError] = useState<string | null>(null);
  const sourceLoadSeq = useRef(0);
  const appliedResetKey = useRef(resetKey);

  useEffect(() => {
    if (appliedResetKey.current === resetKey) return;
    appliedResetKey.current = resetKey;
    sourceLoadSeq.current += 1;
    setSearchText("");
    setEmployeeOptions([]);
    setSelectedEmployee([]);
    setSource(null);
    setSourceLoading(false);
    setSelectedTeamIds([]);
    setCopyError(null);
  }, [resetKey]);

  useEffect(() => {
    setSearchText("");
    setEmployeeOptions([]);
    setSelectedEmployee([]);
    setSource(null);
    setSelectedTeamIds([]);
    setCopyError(null);
  }, [permissionProject]);

  useEffect(() => {
    const query = debouncedSearch.trim();
    if (query.length < 2) {
      setEmployeeOptions([]);
      return;
    }

    let cancelled = false;
    const search = async () => {
      try {
        const options = await onSearchEmployees(query);
        if (!cancelled) setEmployeeOptions(options);
      } catch (error: any) {
        if (cancelled) return;
        setEmployeeOptions([]);
        setCopyError(
          error?.response?.data?.message || "Couldn't search employees.",
        );
      }
    };

    void search();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, onSearchEmployees]);

  const sourceTeams: CopyTeam[] = (source?.teams ?? []).map((team) => {
    const teamId = Number(team.id);
    const catalog = teamsWithPermissions.find(
      (item) => Number(item.id) === teamId,
    );
    const assignedRoleIds = new Set(
      (source?.permissions ?? []).map((permission) => Number(permission.role_id)),
    );
    const permissions = (catalog?.permissions ?? [])
      .filter((permission) => assignedRoleIds.has(Number(permission.role_id)))
      .map((permission) => ({
        role_id: Number(permission.role_id),
        role: String(permission.role),
      }));
    return {
      id: teamId,
      name: String(team.name),
      is_manager: team.is_manager === true || team.is_manager === 1,
      permissions,
    };
  });

  const clearCopiedSelection = () => {
    const chosen = sourceTeams.filter((team) =>
      selectedTeamIds.includes(team.id),
    );
    if (!chosen.length) return;
    const roleIds = new Set<number>();
    chosen.forEach((team) => {
      team.permissions.forEach((permission) => roleIds.add(permission.role_id));
    });
    onCopyTeamsChange({
      addTeams: [],
      addPermissions: [],
      removeTeamIds: chosen.map((team) => team.id),
      removeRoleIds: Array.from(roleIds),
    });
    setSelectedTeamIds([]);
  };

  const handleEmployeeSelect = async (selected: FilterValue[]) => {
    clearCopiedSelection();
    setSelectedEmployee(selected);
    setCopyError(null);
    const employeeId = Number(selected[0]?.id);
    const requestId = ++sourceLoadSeq.current;
    if (!employeeId) {
      setSource(null);
      setSourceLoading(false);
      return;
    }

    try {
      setSourceLoading(true);
      const employee = await onLoadEmployee(employeeId);
      if (requestId !== sourceLoadSeq.current) return;
      const teamIds = (employee.teams ?? [])
        .map((team) => Number(team.id))
        .filter((teamId) => teamId > 0);
      if (onEnsureTeamPermissions && teamIds.length) {
        await onEnsureTeamPermissions(teamIds);
      }
      if (requestId !== sourceLoadSeq.current) return;
      setSource(employee);
      if (!(employee.teams ?? []).length) {
        setCopyError("This employee is not assigned to any team.");
      }
    } catch (error: any) {
      if (requestId !== sourceLoadSeq.current) return;
      setSource(null);
      setCopyError(
        error?.response?.data?.message ||
          "Couldn't load that employee's permissions.",
      );
    } finally {
      if (requestId === sourceLoadSeq.current) setSourceLoading(false);
    }
  };

  const toggleSourceTeam = (teamId: number) => {
    const team = sourceTeams.find((item) => item.id === teamId);
    if (!team || disabled) return;
    const selecting = !selectedTeamIds.includes(teamId);
    const nextSelectedIds = selecting
      ? [...selectedTeamIds, teamId]
      : selectedTeamIds.filter((id) => id !== teamId);
    const retainedRoleIds = new Set<number>();
    sourceTeams.forEach((item) => {
      if (item.id === team.id || !nextSelectedIds.includes(item.id)) return;
      item.permissions.forEach((permission) => {
        retainedRoleIds.add(permission.role_id);
      });
    });
    onCopyTeamsChange({
      addTeams: selecting
        ? [{ id: team.id, name: team.name, is_manager: team.is_manager }]
        : [],
      addPermissions: selecting ? team.permissions : [],
      removeTeamIds: selecting ? [] : [team.id],
      removeRoleIds: selecting
        ? []
        : team.permissions
            .map((permission) => permission.role_id)
            .filter((roleId) => !retainedRoleIds.has(roleId)),
    });
    setSelectedTeamIds(nextSelectedIds);
    setCopyError(null);
  };

  return (
    <Box
      sx={{
        mb: 3,
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.customColors.greyBorder}`,
        backgroundColor: theme.palette.customColors.white,
      }}
    >
      <CustomTypography variant="heading6" style={{ fontWeight: 600, marginBottom: 12 }}>
        Copy permissions from
      </CustomTypography>
      <AppSearchDropdownMulti
        borderColor={theme.palette.customColors.blackBorder}
        isMultiSelect={false}
        title="Select employee"
        fontSize="0.8rem"
        local={false}
        floatingLabel
        value={selectedEmployee}
        list={employeeOptions}
        handleSearch={(text) => setSearchText(text || "")}
        handleSelect={(selected) => {
          void handleEmployeeSelect(selected);
        }}
        disabled={disabled}
      />

      {sourceLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
          <CircularProgress size={16} />
          <CustomTypography
            variant="body3"
            color={theme.palette.customColors.greyTextColor}
          >
            Loading teams...
          </CustomTypography>
        </Box>
      ) : null}

      {!sourceLoading && source && sourceTeams.length > 0 ? (
        <Box sx={{ mt: 1.5 }}>
          <CustomTypography
            variant="body2"
            style={{ fontWeight: 600, marginBottom: 8 }}
          >
            {source.name}'s teams
          </CustomTypography>
          <Grid2 container spacing={1}>
            {sourceTeams.map((team) => {
              const checked = selectedTeamIds.includes(team.id);
              return (
                <Grid2 key={team.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Box
                    onClick={() => {
                      if (!disabled) toggleSourceTeam(team.id);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      border: `1px solid ${
                        checked
                          ? theme.palette.primary.main
                          : theme.palette.customColors.greyBorder
                      }`,
                      backgroundColor: checked
                        ? theme.palette.customColors.lightGreyColor
                        : theme.palette.customColors.white,
                      cursor: disabled ? "default" : "pointer",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled}
                      sx={{ pointerEvents: "none", p: 0.5 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <CustomTypography variant="body2">
                        {team.name}
                      </CustomTypography>
                      <CustomTypography
                        variant="body3"
                        color={theme.palette.customColors.greyTextColor}
                      >
                        {team.permissions.length}{" "}
                        {team.permissions.length === 1
                          ? "permission"
                          : "permissions"}
                      </CustomTypography>
                    </Box>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        </Box>
      ) : null}

      {copyError ? <InlineFormError message={copyError} /> : null}
    </Box>
  );
};

export default CopyEmployeePermissions;
